import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { usersApi, specialtiesApi, companyAccountsApi, profileApi, adminRegistrationsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Pencil, Eye, EyeOff, LogIn, X, Building2, Users, Trash2, Upload, FileText, Download, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';
import { SpecialtyMultiSelect } from '../components/SpecialtyMultiSelect';
import { getRoleColor, getRoleLabel, formatDate } from '../lib/utils';
import PendingRegistrations from './PendingRegistrations';

// Helper to extract error message from API response
const getErrorMessage = (error) => {
  const detail = error.response?.data?.detail;
  if (!detail) return 'An error occurred';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
  }
  if (typeof detail === 'object' && detail.msg) return detail.msg;
  return 'An error occurred';
};

export default function AdminUsers() {
  const { isAdmin, isCRD, isChairman, user: currentUser, impersonateUser, impersonateCompany } = useAuth();
  const navigate = useNavigate();
  // iter150 — Chairman can now approve/reject pending registrations. They land
  // directly on the Pending tab and never see the Users / Companies management
  // tabs (which remain Admin-only).
  const chairmanOnly = isChairman() && !isAdmin();
  const [activeTab, setActiveTab] = useState(chairmanOnly ? 'pending' : 'users');
  
  // Users state
  const [users, setUsers] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [companyAccounts, setCompanyAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  
  // User modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  // Role doc config & profile docs for edit modal
  const [roleDocConfigs, setRoleDocConfigs] = useState({});
  const [editUserDocs, setEditUserDocs] = useState([]);
  const [showDocUpload, setShowDocUpload] = useState(false);
  const [docUploadType, setDocUploadType] = useState('');
  const [docUploadHasExpiry, setDocUploadHasExpiry] = useState(false);
  const [docUploadExpiry, setDocUploadExpiry] = useState('');
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docUploading, setDocUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    password: '',
    role: '',
    phone: '',
    specialty_ids: [],
    company_account_id: '',
    is_active: true,
    visible_request_types: null  // null = role-default visibility; array = override
  });

  // Company modal states
  const [showCompanyModal, setShowCompanyModal] = useState(false);
  const [editingCompany, setEditingCompany] = useState(null);
  const [companySearch, setCompanySearch] = useState('');
  const [userSpecSearch, setUserSpecSearch] = useState('');
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    contact_email: '',
    phone: '',
    address: '',
    website: '',
    industry: '',
    tax_id: '',
    status: 'active'
  });

  // Pending registrations state
  const [pending, setPending] = useState({ users: [], companies: [], total: 0 });
  const [pendingLoading, setPendingLoading] = useState(false);
  const pendingTotal = pending?.total || 0;

  const fetchPending = async () => {
    if (!isAdmin() && !isChairman()) return;
    setPendingLoading(true);
    try {
      const res = await adminRegistrationsApi.getPending();
      setPending(res.data || { users: [], companies: [], total: 0 });
    } catch (e) {
      console.error('Failed to fetch pending registrations', e);
    } finally {
      setPendingLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchPending();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, specsRes] = await Promise.all([
        usersApi.getAll(),
        specialtiesApi.getAll()
      ]);
      setUsers(usersRes.data);
      setSpecialties(specsRes.data);
      
      if (isAdmin() || isCRD()) {
        // iter121 — fetch FEA + CTA + FT and KEEP them grouped per request type so the
        // Edit User dialog can render "FT Requirements / CTA Requirements / FEA
        // Requirements" sub-headers instead of a flat merged list.
        const [companiesRes, feaDocRes, ctaDocRes, ftDocRes] = await Promise.all([
          companyAccountsApi.getAll(),
          profileApi.getRoleDocConfigsByType('FEA').catch(() => ({ data: [] })),
          profileApi.getRoleDocConfigsByType('CTA').catch(() => ({ data: [] })),
          profileApi.getRoleDocConfigsByType('FT').catch(() => ({ data: [] }))
        ]);
        setCompanyAccounts(companiesRes.data);
        // Build role -> { FT: [...], CTA: [...], FEA: [...] } map (dedupe by name within a group).
        const configMap = {};
        const types = [['FT', ftDocRes.data || []], ['CTA', ctaDocRes.data || []], ['FEA', feaDocRes.data || []]];
        for (const [rt, configs] of types) {
          for (const c of configs) {
            if (!configMap[c.role]) configMap[c.role] = { FT: [], CTA: [], FEA: [] };
            for (const dt of (c.document_types || [])) {
              if (!configMap[c.role][rt].some(d => d.name === dt.name)) {
                configMap[c.role][rt].push(dt);
              }
            }
          }
        }
        setRoleDocConfigs(configMap);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ========== USER HANDLERS ==========
  const resetForm = () => {
    setFormData({
      email: '',
      name: '',
      password: '',
      role: '',
      phone: '',
      specialty_ids: [],
      company_account_id: '',
      is_active: true,
      visible_request_types: null
    });
    setShowPassword(false);
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.email || !formData.password || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate company selection for COMPANY_REP
    if (formData.role === 'COMPANY_REP' && !formData.company_account_id) {
      toast.error('Please select a company for the Company Rep');
      return;
    }
    
    try {
      await usersApi.create(formData);
      toast.success('User created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleEditClick = async (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      name: user.name,
      password: '',
      role: user.role,
      phone: user.phone || '',
      specialty_ids: user.specialty_ids || [],
      company_account_id: user.company_account_id || '',
      is_active: user.is_active,
      visible_request_types: user.visible_request_types ?? null
    });
    setEditUserDocs(user.profile_documents || []);
    setShowEditModal(true);
    // Fetch full profile for latest docs
    try {
      const res = await profileApi.getUserProfile(user.id);
      setEditUserDocs(res.data.profile_documents || []);
    } catch (e) {}
  };

  // Admin upload doc on behalf of user
  const handleAdminDocUpload = async () => {
    if (!docUploadFile) { toast.error('Select a file'); return; }
    if (docUploadHasExpiry && !docUploadExpiry) { toast.error('Expiry date is required'); return; }
    setDocUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', docUploadFile);
      formData.append('doc_type', docUploadType);
      if (docUploadHasExpiry && docUploadExpiry) formData.append('expires_at', docUploadExpiry);
      await profileApi.uploadUserDocument(editingUser.id, formData);
      toast.success(`${docUploadType} uploaded`);
      setShowDocUpload(false);
      const res = await profileApi.getUserProfile(editingUser.id);
      setEditUserDocs(res.data.profile_documents || []);
      fetchData();
    } catch (error) {
      toast.error('Failed to upload');
    } finally { setDocUploading(false); }
  };

  const handleAdminDocDelete = async (docId, docType) => {
    if (!confirm(`Delete ${docType}?`)) return;
    try {
      await profileApi.deleteDocument(docId);
      toast.success('Deleted');
      const res = await profileApi.getUserProfile(editingUser.id);
      setEditUserDocs(res.data.profile_documents || []);
      fetchData();
    } catch (error) { toast.error('Failed to delete'); }
  };

  const handleUpdate = async () => {
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate company selection for COMPANY_REP
    if (formData.role === 'COMPANY_REP' && !formData.company_account_id) {
      toast.error('Please select a company for the Company Rep');
      return;
    }
    
    try {
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        phone: formData.phone || null,
        specialty_ids: formData.specialty_ids,
        company_account_id: formData.role === 'COMPANY_REP' ? formData.company_account_id : null,
        is_active: formData.is_active,
        visible_request_types: formData.visible_request_types
      };
      
      if (formData.password && formData.password.trim() !== '') {
        updateData.password = formData.password;
      }
      
      await usersApi.update(editingUser.id, updateData);
      toast.success('User updated successfully');
      setShowEditModal(false);
      setEditingUser(null);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleImpersonate = async (user) => {
    if (user.id === currentUser?.id) {
      toast.error('Cannot impersonate yourself');
      return;
    }
    
    try {
      await impersonateUser(user.id);
      toast.success(`Now viewing as ${user.name}`);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate user');
    }
  };

  const handleImpersonateCompany = async (company) => {
    try {
      const userData = await impersonateCompany(company.id);
      toast.success(`Now viewing as ${company.name}`);
      navigate('/dashboard');
      // Refresh users list so the newly auto-provisioned owner shows up
      fetchData();
      return userData;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate company');
    }
  };

  const toggleSpecialty = (specialtyId) => {
    setFormData(prev => {
      const ids = prev.specialty_ids || [];
      if (ids.includes(specialtyId)) {
        return { ...prev, specialty_ids: ids.filter(id => id !== specialtyId) };
      } else {
        return { ...prev, specialty_ids: [...ids, specialtyId] };
      }
    });
  };

  // ========== COMPANY HANDLERS ==========
  const resetCompanyForm = () => {
    setCompanyFormData({
      name: '',
      contact_email: '',
      phone: '',
      address: '',
      website: '',
      industry: '',
      tax_id: '',
      status: 'active',
      _new_password: ''
    });
    setEditingCompany(null);
  };

  const handleCreateCompany = async () => {
    if (!companyFormData.name || !companyFormData.contact_email) {
      toast.error('Please fill in company name and email');
      return;
    }
    if (!companyFormData._new_password || companyFormData._new_password.length < 6) {
      toast.error('Please set a password for the company login (min 6 characters)');
      return;
    }

    try {
      const { _new_password, ...payload } = companyFormData;
      const res = await companyAccountsApi.create(payload);
      const newCompanyId = res?.data?.id;
      // Provision the company login (auto-creates the owner user with this password)
      if (newCompanyId) {
        await companyAccountsApi.setPassword(newCompanyId, _new_password);
      }
      toast.success('Company created — they can now sign in with their contact email');
      setShowCompanyModal(false);
      resetCompanyForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleEditCompanyClick = (company) => {
    setEditingCompany(company);
    setCompanyFormData({
      name: company.name,
      contact_email: company.contact_email,
      phone: company.phone || '',
      address: company.address || '',
      website: company.website || '',
      industry: company.industry || '',
      tax_id: company.tax_id || '',
      status: company.status || 'active'
    });
    setShowCompanyModal(true);
  };

  const handleUpdateCompany = async () => {
    if (!companyFormData.name || !companyFormData.contact_email) {
      toast.error('Please fill in company name and email');
      return;
    }
    
    try {
      await companyAccountsApi.update(editingCompany.id, companyFormData);
      toast.success('Company updated successfully');
      setShowCompanyModal(false);
      resetCompanyForm();
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteCompany = async (company) => {
    const cascadeMsg = company.reps_count > 0
      ? `\n\nThis will ALSO permanently delete ${company.reps_count} associated user account${company.reps_count === 1 ? '' : 's'} (the company login + its reps).`
      : '';
    if (!window.confirm(`Are you sure you want to delete "${company.name}"?${cascadeMsg}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      const res = await companyAccountsApi.delete(company.id);
      const deleted = res?.data?.deleted_users_count || 0;
      toast.success(deleted > 0
        ? `Company deleted (${deleted} user account${deleted === 1 ? '' : 's'} also removed)`
        : 'Company deleted successfully');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleDeleteUser = async (target) => {
    if (target.id === currentUser?.id) {
      toast.error('You cannot delete your own account');
      return;
    }
    if (target.is_company_owner) {
      toast.error('This is a Company Account owner. Delete the Company itself to remove it (it will cascade).');
      return;
    }
    if (!window.confirm(`Delete user "${target.name}" (${target.email})? This cannot be undone.`)) return;
    try {
      await usersApi.delete(target.id);
      toast.success('User deleted');
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  // ========== FILTERING ==========
  // Company-side users (COMPANY_REP) are now managed exclusively under the
  // "Companies & CROs" tab and excluded from the generic Users tab.
  const filteredUsers = users.filter(user => {
    if (user.role === 'COMPANY_REP') return false;
    if (roleFilter && user.role !== roleFilter) return false;
    if (search) {
      const s = search.toLowerCase();
      return user.name.toLowerCase().includes(s) || user.email.toLowerCase().includes(s);
    }
    return true;
  });

  // Reps grouped by company_account_id for the hierarchical Companies tab
  const repsByCompany = users.reduce((acc, u) => {
    if (u.role === 'COMPANY_REP' && u.company_account_id) {
      (acc[u.company_account_id] = acc[u.company_account_id] || []).push(u);
    }
    return acc;
  }, {});

  // Owner user (one per company) — used for "impersonate company" action
  const ownerByCompany = users.reduce((acc, u) => {
    if (u.role === 'COMPANY_REP' && u.is_company_owner && u.company_account_id) {
      acc[u.company_account_id] = u;
    }
    return acc;
  }, {});

  const filteredCompanies = companyAccounts.filter(company => {
    if (!companySearch) return true;
    const s = companySearch.toLowerCase();
    if (company.name.toLowerCase().includes(s)) return true;
    if (company.contact_email && company.contact_email.toLowerCase().includes(s)) return true;
    if (company.industry && company.industry.toLowerCase().includes(s)) return true;
    // Match against this company's reps too
    const linkedReps = repsByCompany[company.id] || [];
    return linkedReps.some(r =>
      (r.name && r.name.toLowerCase().includes(s)) ||
      (r.email && r.email.toLowerCase().includes(s))
    );
  });

  // Roles available when creating users.
  // COMPANY_REP is included so the "Add Company Rep" button (Companies & CROs tab) works,
  // but the Users tab filters it out of the listing and role-filter UI.
  const roles = ['COMPANY_REP', 'CHAIRMAN', 'COORDINATOR', 'DIRECTOR', 'EXEC_DIRECTOR', 'FINANCE', 'INVESTIGATOR', 'REVIEWER', 'CRD', 'ADMIN'];
  // For filtering on the Users tab — COMPANY_REP excluded (managed under Companies & CROs)
  const filterRoles = ['CHAIRMAN', 'COORDINATOR', 'DIRECTOR', 'EXEC_DIRECTOR', 'FINANCE', 'INVESTIGATOR', 'PI', 'CONSULTANT', 'REVIEWER', 'CRD', 'ADMIN'];
  // REVIEWER intentionally excluded — reviewers do not require specialty tagging.
  const rolesWithSpecialties = ['INVESTIGATOR', 'PI', 'CONSULTANT'];

  // Get company name for a user
  const getCompanyName = (companyAccountId) => {
    const company = companyAccounts.find(c => c.id === companyAccountId);
    return company ? company.name : null;
  };

  // ========== RENDER FORM FIELDS ==========
  const renderFormFields = (isEdit) => {
    const showSpecialties = rolesWithSpecialties.includes(formData.role);
    const showCompanySelect = formData.role === 'COMPANY_REP';
    
    return (
      <div className="space-y-4">
        <div>
          <Label>Name <span className="text-red-500">*</span></Label>
          <Input
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Full name"
            data-testid="user-name-input"
          />
        </div>
        <div>
          <Label>Email <span className="text-red-500">*</span></Label>
          <Input
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            placeholder="email@example.com"
            data-testid="user-email-input"
          />
        </div>
        <div>
          <Label>Phone</Label>
          <Input
            type="tel"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="05XXXXXXXX"
            data-testid="user-phone-input"
          />
        </div>
        <div>
          <Label>
            Password {!isEdit && <span className="text-red-500">*</span>}
            {isEdit && <span className="text-slate-400 text-sm ml-1">(leave blank to keep current)</span>}
          </Label>
          <div className="relative">
            <Input
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
              placeholder={isEdit ? "Enter new password to change" : "Enter password"}
              className="pr-10"
              data-testid="user-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              data-testid="toggle-password-visibility"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
          {isEdit && editingUser && (
            <div className="mt-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={async () => {
                  if (!confirm(`Generate a new random password and email it to ${editingUser.email}?`)) return;
                  try {
                    await adminRegistrationsApi.resetUserPassword(editingUser.id);
                    toast.success('Password reset and emailed to the user');
                  } catch (e) { toast.error(getErrorMessage(e)); }
                }}
                data-testid="user-reset-password-btn"
              >
                Reset & email new password
              </Button>
            </div>
          )}
        </div>
        <div>
          <Label>Role <span className="text-red-500">*</span></Label>
          {formData.role === 'COMPANY_REP' ? (
            <Input
              value="Company Representative"
              disabled
              data-testid="user-role-locked"
            />
          ) : (
            <Select 
              value={formData.role} 
              onValueChange={(v) => {
                // Auto-pre-fill visible_request_types from role default when role changes
                // (only for new users — keep existing override on edit)
                const roleDefault = (() => {
                  const high = ['ADMIN','CRD','CHAIRMAN','DIRECTOR','EXEC_DIRECTOR'];
                  if (high.includes(v)) return ['FEA','CTA','FT'];
                  if (v === 'FINANCE') return ['FT'];
                  if (v === 'COMPANY_REP') return ['FEA','CTA'];
                  return ['FEA','CTA','FT'];
                })();
                setFormData(prev => ({ 
                  ...prev, 
                  role: v, 
                  specialty_ids: rolesWithSpecialties.includes(v) ? prev.specialty_ids : [],
                  company_account_id: v === 'COMPANY_REP' ? prev.company_account_id : '',
                  visible_request_types: editingUser ? prev.visible_request_types : roleDefault,
                }));
              }}
            >
              <SelectTrigger data-testid="user-role-select">
                <SelectValue placeholder="Select role" />
              </SelectTrigger>
              <SelectContent>
                {roles.filter(r => r !== 'COMPANY_REP').map(role => (
                  <SelectItem key={role} value={role}>{getRoleLabel(role)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        
        {/* Company Select for COMPANY_REP */}
        {showCompanySelect && (
          <div>
            <Label>Company <span className="text-red-500">*</span></Label>
            <Select 
              value={formData.company_account_id} 
              onValueChange={(v) => setFormData(prev => ({ ...prev, company_account_id: v }))}
            >
              <SelectTrigger data-testid="user-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companyAccounts.length === 0 ? (
                  <SelectItem value="none" disabled>No companies available</SelectItem>
                ) : (
                  companyAccounts.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {companyAccounts.length === 0 && (
              <p className="text-sm text-amber-600 mt-1">
                Please create a company first in the Companies tab
              </p>
            )}
          </div>
        )}
        
        {/* Specialties for PI/Investigator/Consultant */}
        {showSpecialties && (
          <div>
            <Label>Specialties <span className="text-slate-400 text-sm">(select multiple)</span></Label>
            <div className="mt-2">
              <SpecialtyMultiSelect
                specialties={specialties}
                value={formData.specialty_ids || []}
                onChange={(ids) => setFormData((prev) => ({ ...prev, specialty_ids: ids }))}
                testidPrefix="user-spec"
              />
            </div>
          </div>
        )}
        
        {isEdit && (
          <div className="pt-3 border-t">
            <Label className="text-sm font-medium mb-2 block">Profile Documents</Label>
            {(() => {
              const grouped = roleDocConfigs[formData.role] || { FT: [], CTA: [], FEA: [] };
              const getDaysUntil = (d) => d ? Math.ceil((new Date(d) - new Date()) / 86400000) : null;
              const types = [['FT', grouped.FT || []], ['CTA', grouped.CTA || []], ['FEA', grouped.FEA || []]];
              const total = (grouped.FT?.length || 0) + (grouped.CTA?.length || 0) + (grouped.FEA?.length || 0);
              if (total === 0) return <p className="text-xs text-slate-400">No document requirements configured for this role</p>;
              return (
                <div className="space-y-3">
                  {types.map(([rt, reqDocs]) => {
                    if (!reqDocs || reqDocs.length === 0) return null;
                    return (
                      <div key={rt} className="space-y-2">
                        <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 flex items-center gap-1.5">
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-[10px] px-1.5 py-0">{rt}</Badge>
                          <span>{rt} Requirements</span>
                        </div>
                        {reqDocs.map((reqDoc, i) => {
                          const uploaded = editUserDocs.find(d => d.doc_type === reqDoc.name);
                          const expDays = uploaded?.expires_at ? getDaysUntil(uploaded.expires_at) : null;
                          const isExpired = expDays !== null && expDays <= 0;
                          const isExpiring = expDays !== null && expDays > 0 && expDays <= 30;
                          return (
                            <div key={`${rt}-${i}`} className={`p-2.5 rounded-md border text-sm ${uploaded ? (isExpired ? 'border-red-200 bg-red-50' : isExpiring ? 'border-amber-200 bg-amber-50' : 'border-emerald-200 bg-emerald-50') : 'border-slate-200 bg-slate-50'}`}>
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  {uploaded ? (isExpired ? <AlertTriangle className="w-4 h-4 text-red-500" /> : isExpiring ? <Clock className="w-4 h-4 text-amber-500" /> : <CheckCircle2 className="w-4 h-4 text-emerald-500" />) : <Upload className="w-4 h-4 text-slate-400" />}
                                  <div>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-medium text-xs">{reqDoc.name}</span>
                                      {reqDoc.enforce === 'block_assign' && <Badge className="bg-red-100 text-red-600 text-[9px] px-1 py-0">Required</Badge>}
                                    </div>
                                    {uploaded ? (
                                      <p className="text-[10px] text-slate-500">{uploaded.filename}{uploaded.expires_at ? ` · Exp: ${new Date(uploaded.expires_at).toLocaleDateString()}${isExpired ? ' (EXPIRED)' : isExpiring ? ` (${expDays}d)` : ''}` : ''}</p>
                                    ) : (
                                      <p className="text-[10px] text-slate-400">Not uploaded</p>
                                    )}
                                  </div>
                                </div>
                                <div className="flex gap-1">
                                  {uploaded && <Button variant="ghost" size="sm" className="h-6 px-1.5" onClick={() => handleAdminDocDelete(uploaded.id, reqDoc.name)}><Trash2 className="w-3 h-3 text-red-400" /></Button>}
                                  <Button variant="outline" size="sm" className="h-6 px-2 text-[10px]" onClick={() => {
                                    if (uploaded) {
                                      if (!window.confirm(`This will replace ${editingUser?.name || 'this user'}'s existing ${reqDoc.name}. The previous file will be permanently deleted. Continue?`)) {
                                        return;
                                      }
                                    }
                                    setDocUploadType(reqDoc.name);
                                    setDocUploadHasExpiry(reqDoc.has_expiry);
                                    setDocUploadExpiry('');
                                    setDocUploadFile(null);
                                    setShowDocUpload(true);
                                  }}>
                                    <Upload className="w-3 h-3 mr-0.5" /> {uploaded ? 'Replace' : 'Upload'}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    );
  };

  // ========== RENDER COMPANY FORM ==========
  const renderCompanyForm = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Company Name <span className="text-red-500">*</span></Label>
          <Input
            value={companyFormData.name}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, name: e.target.value }))}
            placeholder="Company name"
            data-testid="company-name-input"
          />
        </div>
        <div>
          <Label>Contact Email <span className="text-red-500">*</span></Label>
          <Input
            type="email"
            value={companyFormData.contact_email}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, contact_email: e.target.value }))}
            placeholder="contact@company.com"
            data-testid="company-email-input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Phone</Label>
          <Input
            value={companyFormData.phone}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, phone: e.target.value }))}
            placeholder="+1 234 567 8900"
            data-testid="company-phone-input"
          />
        </div>
        <div>
          <Label>Website</Label>
          <Input
            value={companyFormData.website}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, website: e.target.value }))}
            placeholder="https://company.com"
            data-testid="company-website-input"
          />
        </div>
      </div>
      <div>
        <Label>Address</Label>
        <Input
          value={companyFormData.address}
          onChange={(e) => setCompanyFormData(prev => ({ ...prev, address: e.target.value }))}
          placeholder="Full address"
          data-testid="company-address-input"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Industry</Label>
          <Input
            value={companyFormData.industry}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, industry: e.target.value }))}
            placeholder="e.g., Pharmaceutical, Biotech"
            data-testid="company-industry-input"
          />
        </div>
        <div>
          <Label>Tax ID</Label>
          <Input
            value={companyFormData.tax_id}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, tax_id: e.target.value }))}
            placeholder="Tax identification number"
            data-testid="company-taxid-input"
          />
        </div>
      </div>
      {editingCompany && (
        <div>
          <Label>Status</Label>
          <Select value={companyFormData.status} onValueChange={(v) => setCompanyFormData(prev => ({ ...prev, status: v }))}>
            <SelectTrigger data-testid="company-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      {!editingCompany && (
        <div className="border-t pt-4 space-y-2" data-testid="company-create-password-section">
          <Label>Company Login Password <span className="text-red-500">*</span></Label>
          <p className="text-xs text-slate-500">The company will use the contact email above + this password to sign in. Minimum 6 characters.</p>
          <PasswordInput
            placeholder="Minimum 6 characters"
            value={companyFormData._new_password || ''}
            onChange={(e) => setCompanyFormData(prev => ({ ...prev, _new_password: e.target.value }))}
            data-testid="company-create-password-input"
          />
        </div>
      )}
      {editingCompany && (
        <div className="border-t pt-4 space-y-2" data-testid="company-password-section">
          <Label>Company Login Password</Label>
          <p className="text-xs text-slate-500">Set a password so this company can sign in directly (separately from its reps), or generate a new random one and email it to the contact.</p>
          <div className="flex gap-2">
            <PasswordInput
              placeholder="Type a new password (min 6 chars)"
              value={companyFormData._new_password || ''}
              onChange={(e) => setCompanyFormData(prev => ({ ...prev, _new_password: e.target.value }))}
              className="flex-1"
              data-testid="company-set-password-input"
            />
            <Button
              type="button"
              variant="outline"
              onClick={async () => {
                const pw = companyFormData._new_password || '';
                if (pw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
                try {
                  await companyAccountsApi.setPassword(editingCompany.id, pw);
                  toast.success('Company password updated');
                  setCompanyFormData(prev => ({ ...prev, _new_password: '' }));
                } catch (e) { toast.error(getErrorMessage(e)); }
              }}
              data-testid="company-set-password-btn"
            >
              Save password
            </Button>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              if (!confirm(`Generate a new random password and email it to ${editingCompany.contact_email}?`)) return;
              try {
                await companyAccountsApi.resetPassword(editingCompany.id);
                toast.success('Password reset and emailed to the contact email');
              } catch (e) { toast.error(getErrorMessage(e)); }
            }}
            data-testid="company-reset-password-btn"
          >
            Reset & email new password
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <DashboardLayout title={chairmanOnly ? 'Pending Registrations' : 'User Management'}>
      <div className="space-y-6" data-testid="admin-users">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="flex items-center justify-between">
            <TabsList>
              {!chairmanOnly && (
                <TabsTrigger value="users" className="gap-2">
                  <Users className="w-4 h-4" />
                  Internal Users
                </TabsTrigger>
              )}
              {!chairmanOnly && (
                <TabsTrigger value="companies" className="gap-2">
                  <Building2 className="w-4 h-4" />
                  Companies & CROs
                </TabsTrigger>
              )}
              {(isAdmin() || isChairman()) && (
                <TabsTrigger value="pending" className="gap-2" data-testid="pending-tab">
                  <Clock className="w-4 h-4" />
                  Pending
                  {pendingTotal > 0 && (
                    <Badge variant="default" className="ml-1 bg-amber-500 hover:bg-amber-500" data-testid="pending-badge">
                      {pendingTotal}
                    </Badge>
                  )}
                </TabsTrigger>
              )}
            </TabsList>
            
            {activeTab === 'users' && isAdmin() && (
              <Button onClick={() => { resetForm(); setShowCreateModal(true); }} className="bg-blue-900 hover:bg-blue-950" data-testid="add-user-btn">
                <Plus size={16} className="mr-2" />
                Add User
              </Button>
            )}
            {activeTab === 'companies' && isAdmin() && (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => {
                    resetForm();
                    setFormData(prev => ({ ...prev, role: 'COMPANY_REP', is_active: true }));
                    setShowCreateModal(true);
                  }}
                  variant="outline"
                  data-testid="add-company-rep-btn"
                >
                  <Plus size={16} className="mr-2" />
                  Add Company Rep
                </Button>
                <Button onClick={() => { resetCompanyForm(); setShowCompanyModal(true); }} className="bg-blue-900 hover:bg-blue-950" data-testid="add-company-btn">
                  <Plus size={16} className="mr-2" />
                  Add Company
                </Button>
              </div>
            )}
          </div>

          {/* ========== USERS TAB ========== */}
          <TabsContent value="users" className="mt-6">
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="pl-10"
                        data-testid="search-users-input"
                      />
                    </div>
                  </div>
                  <Select value={roleFilter || "all"} onValueChange={(value) => setRoleFilter(value === "all" ? "" : value)}>
                    <SelectTrigger className="w-[180px]" data-testid="filter-role-select">
                      <SelectValue placeholder="Filter by role" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Roles</SelectItem>
                      {filterRoles.map(role => (
                        <SelectItem key={role} value={role}>{getRoleLabel(role)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Scope</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map(user => (
                      <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            {user.name}
                            {(() => {
                              // iter121 — roleDocConfigs is now { FT, CTA, FEA } per role.
                              // Flatten + dedupe by name for the missing-docs badge check.
                              const grouped = roleDocConfigs[user.role] || { FT: [], CTA: [], FEA: [] };
                              const flat = [];
                              const seen = new Set();
                              for (const list of [grouped.FT || [], grouped.CTA || [], grouped.FEA || []]) {
                                for (const d of list) {
                                  if (!seen.has(d.name)) { flat.push(d); seen.add(d.name); }
                                }
                              }
                              const userDocs = (user.profile_documents || []).map(d => d.doc_type);
                              const blocking = flat.filter(d => d.enforce === 'block_assign' && !userDocs.includes(d.name));
                              if (blocking.length > 0) return <Badge className="bg-red-100 text-red-600 text-[9px] px-1 py-0" title={`Missing: ${blocking.map(d=>d.name).join(', ')}`}>{blocking.length} doc{blocking.length > 1 ? 's' : ''} missing</Badge>;
                              return null;
                            })()}
                          </div>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge className={getRoleColor(user.role, user)}>
                            {getRoleLabel(user.role, null, user)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {user.role === 'COMPANY_REP' ? (
                            user.company_account_id ? (
                              <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                <Building2 className="w-3 h-3 mr-1" />
                                {getCompanyName(user.company_account_id)}
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                No company linked
                              </Badge>
                            )
                          ) : (
                            <div className="flex flex-wrap gap-1 max-w-xs">
                              {user.specialty_names && user.specialty_names.length > 0 ? (
                                user.specialty_names.map((name, idx) => (
                                  <Badge key={idx} variant="outline" className="text-xs">
                                    {name}
                                  </Badge>
                                ))
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge className={user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                            {user.is_active ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                        <TableCell>{formatDate(user.created_at)}</TableCell>
                        {isAdmin() && (
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={() => handleEditClick(user)}
                                data-testid={`edit-user-${user.id}`}
                                title="Edit user"
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              {user.id !== currentUser?.id && (
                                <Button 
                                  variant="outline" 
                                  size="sm" 
                                  onClick={() => handleImpersonate(user)}
                                  className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                                  data-testid={`impersonate-user-${user.id}`}
                                  title="Login as this user"
                                >
                                  <LogIn className="w-4 h-4" />
                                </Button>
                              )}
                              {user.id !== currentUser?.id && !user.is_company_owner && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDeleteUser(user)}
                                  className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                  data-testid={`delete-user-${user.id}`}
                                  title="Delete user"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <p className="text-slate-500 mt-2 text-sm">{filteredUsers.length} users found</p>
          </TabsContent>

          {/* ========== COMPANIES TAB ========== */}
          <TabsContent value="companies" className="mt-6">
            <Card className="mb-4">
              <CardContent className="p-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Search companies, reps (by name/email)..."
                    value={companySearch}
                    onChange={(e) => setCompanySearch(e.target.value)}
                    className="pl-10"
                    data-testid="search-companies-input"
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Type / Industry</TableHead>
                      <TableHead>Phone</TableHead>
                      <TableHead>Status</TableHead>
                      {isAdmin() && <TableHead className="text-right">Actions</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredCompanies.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                          No companies found. Click &quot;Add Company&quot; to create one.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredCompanies.flatMap(company => {
                        const reps = (repsByCompany[company.id] || [])
                          .filter(r => !r.is_company_owner)
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));

                        const rows = [
                          <TableRow key={`c-${company.id}`} className="bg-slate-50/40" data-testid={`company-row-${company.id}`}>
                            <TableCell className="font-semibold">
                              <div className="flex items-center gap-2">
                                <Building2 className="w-4 h-4 text-slate-500" />
                                {company.name}
                                <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300 text-xs">Company</Badge>
                                {reps.length > 0 && (
                                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                                    {reps.length} rep{reps.length === 1 ? '' : 's'}
                                  </Badge>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>{company.contact_email}</TableCell>
                            <TableCell>
                              {company.industry ? (
                                <Badge variant="outline">{company.industry}</Badge>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell>{company.phone || '-'}</TableCell>
                            <TableCell>
                              <Badge className={company.status === 'active' ? 'bg-emerald-100 text-emerald-700' : (company.status === 'pending' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-500')}>
                                {company.status === 'active' ? 'Active' : (company.status === 'pending' ? 'Pending' : 'Inactive')}
                              </Badge>
                            </TableCell>
                            {isAdmin() && (
                              <TableCell className="text-right">
                                <div className="flex items-center justify-end gap-2">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleEditCompanyClick(company)}
                                    data-testid={`edit-company-${company.id}`}
                                    title="Edit company"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {ownerByCompany[company.id]?.id !== currentUser?.id && (
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleImpersonateCompany(company)}
                                      className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                                      data-testid={`impersonate-company-${company.id}`}
                                      title="Login as this company account"
                                    >
                                      <LogIn className="w-4 h-4" />
                                    </Button>
                                  )}
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => handleDeleteCompany(company)}
                                    className="text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700"
                                    data-testid={`delete-company-${company.id}`}
                                    title="Delete company (cascades to all linked reps)"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ];

                        reps.forEach(rep => {
                          rows.push(
                            <TableRow key={`r-${rep.id}`} data-testid={`company-rep-row-${rep.id}`}>
                              <TableCell>
                                <div className="flex items-center gap-2 pl-8 text-slate-700">
                                  <span className="text-slate-300">└─</span>
                                  {rep.name}
                                </div>
                              </TableCell>
                              <TableCell className="text-slate-600">{rep.email}</TableCell>
                              <TableCell>
                                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">Rep</Badge>
                              </TableCell>
                              <TableCell className="text-slate-500">{rep.phone || '-'}</TableCell>
                              <TableCell>
                                <Badge className={rep.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                                  {rep.is_active ? 'Active' : 'Inactive'}
                                </Badge>
                              </TableCell>
                              {isAdmin() && (
                                <TableCell className="text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleEditClick(rep)}
                                      data-testid={`edit-rep-${rep.id}`}
                                      title="Edit rep"
                                    >
                                      <Pencil className="w-4 h-4" />
                                    </Button>
                                    {rep.id !== currentUser?.id && (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleImpersonate(rep)}
                                        className="text-amber-600 border-amber-300 hover:bg-amber-50 hover:text-amber-700"
                                        data-testid={`impersonate-rep-${rep.id}`}
                                        title="Login as this rep"
                                      >
                                        <LogIn className="w-4 h-4" />
                                      </Button>
                                    )}
                                    <Button
                                      variant="outline"
                                      size="sm"
                                      onClick={() => handleDeleteUser(rep)}
                                      className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                      data-testid={`delete-rep-${rep.id}`}
                                      title="Delete rep"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        });

                        return rows;
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
            <p className="text-slate-500 mt-2 text-sm">
              {filteredCompanies.length} compan{filteredCompanies.length === 1 ? 'y' : 'ies'} ·{' '}
              {filteredCompanies.reduce(
                (sum, c) => sum + ((repsByCompany[c.id] || []).filter(r => !r.is_company_owner).length),
                0
              )} rep{filteredCompanies.reduce((sum, c) => sum + ((repsByCompany[c.id] || []).filter(r => !r.is_company_owner).length), 0) === 1 ? '' : 's'} found
            </p>
          </TabsContent>

          {/* ========== PENDING REGISTRATIONS TAB ========== */}
          {(isAdmin() || isChairman()) && (
            <TabsContent value="pending" className="mt-6">
              <PendingRegistrations
                pending={pending}
                loading={pendingLoading}
                onChanged={async () => { await fetchPending(); await fetchData(); }}
              />
            </TabsContent>
          )}
        </Tabs>

        {/* Create User Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New User</DialogTitle>
              <DialogDescription>Create a new user account</DialogDescription>
            </DialogHeader>
            {renderFormFields(false)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-blue-900 hover:bg-blue-950" data-testid="create-user-btn">
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user details for {editingUser?.name}
              </DialogDescription>
            </DialogHeader>
            {renderFormFields(true)}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditModal(false)}>Cancel</Button>
              <Button onClick={handleUpdate} className="bg-blue-900 hover:bg-blue-950" data-testid="update-user-btn">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Doc Upload Dialog (for admin uploading on behalf) */}
        <Dialog open={showDocUpload} onOpenChange={setShowDocUpload}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Upload {docUploadType}</DialogTitle>
              <DialogDescription>
                Upload on behalf of {editingUser?.name}
                {docUploadHasExpiry ? '. This document requires an expiry date.' : ''}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>File *</Label>
                <Input type="file" onChange={(e) => setDocUploadFile(e.target.files[0])} className="mt-1" />
              </div>
              {docUploadHasExpiry && (
                <div>
                  <Label>Expiry Date *</Label>
                  <Input type="date" value={docUploadExpiry} onChange={(e) => setDocUploadExpiry(e.target.value)} className="mt-1" />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDocUpload(false)}>Cancel</Button>
              <Button onClick={handleAdminDocUpload} disabled={docUploading || !docUploadFile || (docUploadHasExpiry && !docUploadExpiry)} className="bg-blue-900 hover:bg-blue-950">
                {docUploading ? 'Uploading...' : 'Upload'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create/Edit Company Modal */}
        <Dialog open={showCompanyModal} onOpenChange={setShowCompanyModal}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingCompany ? 'Edit Company' : 'Add New Company'}</DialogTitle>
              <DialogDescription>
                {editingCompany ? `Update company details for ${editingCompany.name}` : 'Create a new company account'}
              </DialogDescription>
            </DialogHeader>
            {renderCompanyForm()}
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCompanyModal(false); resetCompanyForm(); }}>Cancel</Button>
              <Button 
                onClick={editingCompany ? handleUpdateCompany : handleCreateCompany} 
                className="bg-blue-900 hover:bg-blue-950" 
                data-testid={editingCompany ? "update-company-btn" : "create-company-btn"}
              >
                {editingCompany ? 'Save Changes' : 'Create Company'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
